/**
 * Активы проекта: созданные результаты.
 *
 * Раздел отвечает на один вопрос, которого нет ни у списка задач, ни у общего
 * списка элементов: **что проект уже сделал и в каком это состоянии**. Задачи
 * говорят о работе, активы — о её итогах.
 *
 * Отсюда три решения в устройстве экрана:
 *
 *   1. Карточки, а не таблица. У актива есть текущая версия, файл, ссылка
 *      наружу и решение проверки — это не пять значений в строке, а короткая
 *      справка, которую читают целиком.
 *
 *   2. Текущая версия видна в списке. Актив без версий — это название без
 *      содержимого, и узнавать об этом открытием карточки поздно: именно так
 *      реестр незаметно наполняется пустышками.
 *
 *   3. Фильтр по состоянию — со счётчиками и сверху. «На проверке» и «нужны
 *      правки» требуют действия сегодня, и это первое, что должно попасться
 *      на глаза.
 *
 * Сама карточка актива — общая карточка элемента: версии, файлы, связи,
 * проверка и обсуждение уже живут там, и второй такой же экран разошёлся бы
 * с первым через месяц.
 */

import {
  ExternalLinkIcon,
  FileIcon,
  HistoryIcon,
  LinkIcon,
  PackageIcon,
  PlusIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { AssetRowDto } from '@project-os/contracts';
import { getItemTypeDefinition } from '@project-os/contracts';
import { useAssets } from '@/api/assets';
import { useProjectMembers } from '@/api/members';
import { useWorkstreams } from '@/api/workstreams';
import { ItemStatusBadge } from '@/components/common/status-badge';
import { CardListSkeleton, EmptyState, ErrorState } from '@/components/common/states';
import { UserAvatar } from '@/components/common/user-avatar';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { formatDate, formatRelative, pluralRu } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ItemDetailPanel } from '../items/ItemDetailPanel';
import { QuickAddDialog } from '../items/QuickAddDialog';
import { useProjectContext } from '../project/use-project-context';

/** Порядок состояний в фильтре — из реестра, а не списком в коде. */
const STATUSES = getItemTypeDefinition('ASSET').statuses;

export function AssetsPage() {
  const project = useProjectContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isPending, isError, error, refetch } = useAssets(project.id);
  const { data: workstreams = [] } = useWorkstreams(project.id);
  const { data: members = [] } = useProjectMembers(project.id);
  const [addOpen, setAddOpen] = useState(false);

  const status = searchParams.get('status');
  const selectedId = searchParams.get('item');

  const setParam = (key: string, value: string | null) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );

  const canEdit = project.permissions.includes('ITEM_WRITE');

  const shown = useMemo(
    () => (data?.assets ?? []).filter((asset) => !status || asset.status === status),
    [data?.assets, status],
  );

  const total = data?.assets.length ?? 0;

  return (
    <>
      <PageHeader
        title="Активы"
        description="Что проект уже создал: документы, дизайн, код, отчёты"
        actions={
          canEdit ? (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <PlusIcon />
              Новый актив
            </Button>
          ) : null
        }
      />

      <PageBody className="space-y-4">
        {isPending ? (
          <CardListSkeleton count={3} />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : total === 0 ? (
          <EmptyState
            icon={<PackageIcon />}
            title="Активов пока нет"
            description={getItemTypeDefinition('ASSET').emptyStateRu}
            hint="К активу прикладывают файл или внешнюю ссылку, а каждая правка становится новой версией — прежняя остаётся."
            action={
              canEdit ? (
                <Button size="sm" onClick={() => setAddOpen(true)}>
                  <PlusIcon />
                  Добавить первый актив
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <StatusFilter
              active={status}
              total={total}
              counts={data.countByStatus}
              onPick={(next) => setParam('status', next)}
            />

            {shown.length === 0 ? (
              <EmptyState
                icon={<PackageIcon />}
                title="В этом состоянии активов нет"
                description="Фильтр можно снять — остальные активы никуда не делись."
                action={
                  <Button size="sm" variant="outline" onClick={() => setParam('status', null)}>
                    Показать все
                  </Button>
                }
              />
            ) : (
              <div className="fade-in-stagger grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {shown.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    onOpen={() => setParam('item', asset.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </PageBody>

      <QuickAddDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={project.id}
        defaultType="ASSET"
        // Созданный актив сразу открывается карточкой: без файла или ссылки
        // он ещё не результат, и следующий шаг очевиден только там.
        onCreated={(id) => setParam('item', id)}
      />

      <ItemDetailPanel
        projectId={project.id}
        itemId={selectedId}
        onClose={() => setParam('item', null)}
        onOpenItem={(id) => setParam('item', id)}
        canEdit={canEdit}
        canReview={project.permissions.includes('REVIEW_WRITE')}
        workstreams={workstreams}
        members={members.map((member) => member.user)}
      />
    </>
  );
}

/**
 * Фильтр по состоянию со счётчиками.
 *
 * Счётчик рядом с названием отвечает на вопрос до нажатия: нажимать на
 * «Нужны правки» с нулём незачем, и человек это видит, а не выясняет.
 */
function StatusFilter({
  active,
  total,
  counts,
  onPick,
}: {
  active: string | null;
  total: number;
  counts: Record<string, number>;
  onPick: (status: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <FilterChip label="Все" count={total} active={active === null} onClick={() => onPick(null)} />
      {STATUSES.map((entry) => {
        const count = counts[entry.value] ?? 0;
        if (count === 0 && active !== entry.value) return null;
        return (
          <FilterChip
            key={entry.value}
            label={entry.labelRu}
            count={count}
            active={active === entry.value}
            onClick={() => onPick(active === entry.value ? null : entry.value)}
          />
        );
      })}
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'press flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
        active
          ? 'border-primary/60 bg-primary-soft font-medium text-foreground'
          : 'border-border/60 text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
      <span className="text-2xs tabular-nums opacity-70">{count}</span>
    </button>
  );
}

/**
 * Карточка одного актива.
 *
 * Читается сверху вниз как ответ на три вопроса: что это, в каком оно
 * состоянии и что за ним лежит. Последняя строка — про происхождение: из
 * какой задачи актив вышел.
 */
function AssetCard({ asset, onOpen }: { asset: AssetRowDto; onOpen: () => void }) {
  const version = asset.currentVersion;

  return (
    <Card className="surface-card-interactive gap-0 overflow-hidden py-0">
      <CardContent className="flex h-full flex-col gap-2.5 p-4">
        <button
          type="button"
          onClick={onOpen}
          className="press -m-1 flex items-start gap-2 rounded p-1 text-left"
        >
          <PackageIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{asset.title}</span>
            {asset.assetTypeLabelRu ? (
              <span className="block text-2xs text-muted-foreground">
                {asset.assetTypeLabelRu}
                {asset.workstream ? ` · ${asset.workstream.name}` : ''}
              </span>
            ) : null}
          </span>
        </button>

        <div className="flex flex-wrap items-center gap-2">
          <ItemStatusBadge type="ASSET" status={asset.status} />
          {asset.lastReview ? (
            <span className="text-2xs text-muted-foreground">
              проверял {asset.lastReview.reviewer.name},{' '}
              {formatRelative(asset.lastReview.createdAt)}
            </span>
          ) : null}
        </div>

        {/* Текущая версия — главное, что отличает актив от заметки с
            названием. Её отсутствие называется прямо, а не оставляется
            пустым местом. */}
        {version ? (
          <div className="rounded-md border border-border/50 bg-muted/40 px-2.5 py-2">
            <div className="flex items-center gap-1.5">
              <HistoryIcon className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate text-xs font-medium">{version.versionLabel}</span>
              <span className="ml-auto shrink-0 text-2xs text-muted-foreground">
                {formatDate(version.createdAt)}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-2xs text-muted-foreground">
              {version.filename ? (
                <>
                  <FileIcon className="size-3 shrink-0" />
                  <span className="truncate">{version.filename}</span>
                </>
              ) : version.externalUrl ? (
                <>
                  <LinkIcon className="size-3 shrink-0" />
                  <span className="truncate">{version.externalUrl}</span>
                </>
              ) : (
                <span>без файла и ссылки</span>
              )}
            </div>
          </div>
        ) : (
          <p className="rounded-md border border-dashed border-border/60 px-2.5 py-2 text-2xs leading-relaxed text-muted-foreground">
            Версий нет: у актива есть название, но нет содержимого. Приложите файл или укажите
            ссылку.
          </p>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-2xs text-muted-foreground">
          {asset.owner ? (
            <span className="flex items-center gap-1.5">
              <UserAvatar user={asset.owner} size="2xs" />
              {asset.owner.name}
            </span>
          ) : (
            <span>без ответственного</span>
          )}

          {asset.versionCount > 0 ? (
            <span className="tabular-nums">
              {pluralRu(asset.versionCount, ['версия', 'версии', 'версий'])}
            </span>
          ) : null}

          {asset.attachmentCount > 0 ? (
            <span className="tabular-nums">
              {pluralRu(asset.attachmentCount, ['файл', 'файла', 'файлов'])}
            </span>
          ) : null}

          {asset.externalUrl ? (
            <a
              href={asset.externalUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="icon-nudge flex items-center gap-1 text-primary hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              снаружи
              <ExternalLinkIcon className="size-3" />
            </a>
          ) : null}
        </div>

        {/* Происхождение: из какой работы вышел результат. Обратная сторона
            связи «задача создала актив». */}
        {asset.producedBy.length > 0 ? (
          <p className="truncate border-t border-border/40 pt-2 text-2xs text-muted-foreground">
            создано в: {asset.producedBy.map((ref) => ref.title).join(', ')}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
